import React from "react";
import { useTranslation } from "react-i18next";
import { momentLocalizer } from "react-big-calendar";
import moment from "moment";
import Modal from "react-modal";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./css/schedule.css";
import { authService } from "../../utils/authService";
import { useUsers } from "../../hooks/useUsers";
import { useSchedules } from "../../hooks/useSchedules";
import { useScheduleForm } from "../../hooks/useScheduleForm";
import { useChecklist } from "../../hooks/useChecklist";
import { ScheduleForm } from "./components/ScheduleForm";
import { ScheduleDetails } from "./components/ScheduleDetails";
import { PhotoSection } from "./components/PhotoSection";
import { AdminViewToggle } from "./components/AdminViewToggle";
import { StatusChip } from "./components/StatusChip";
import { ScheduleCalendarView } from "./components/ScheduleCalendarView";
import { eventPropGetter, modalStyle } from "./utils/constants";
import { usePhotoUpload } from "../../hooks/usePhotoUpload";
import { useScheduleInitialization } from "../../hooks/useScheduleInitialization";

const localizer = momentLocalizer(moment);

const ScheduleCalendar = () => {
  const { t } = useTranslation();
  const currentUser = authService.getUserRole();
  const { users, clients, fetchUsers, fetchClients } = useUsers();

  const {
    schedules,
    selectedSchedule,
    setSelectedSchedule,
    modalIsOpen,
    setModalIsOpen,
    adminViewMode,
    setAdminViewMode,
    fetchSchedules,
    refreshSchedulesIfNeeded,
    handleEventSelect,
    handleCompleteService,
  } = useSchedules(currentUser);

  const { formData, setFormData, handleSubmit } = useScheduleForm(
    selectedSchedule,
    () => {}, // <- ya no recargamos todo el mes al hacer submit
    setModalIsOpen
  );

  const {
    localNotes,
    handleChecklistItemChange,
    handleSaveNotes,
    handleLocalNotesChange,
  } = useChecklist(selectedSchedule, setSelectedSchedule);

  const { handlePhotoUpload } = usePhotoUpload(
    selectedSchedule,
    setSelectedSchedule
  );

  useScheduleInitialization(fetchSchedules, fetchUsers, fetchClients);

  const handleAfterModalClose = async () => {
    if (selectedSchedule?.start_time) {
      const date = new Date(selectedSchedule.start_time);
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      await refreshSchedulesIfNeeded({ month, year });
    }
  };

  return (
    <div style={{ marginTop: "20px" }}>
      <ScheduleCalendarView
        localizer={localizer}
        schedules={schedules}
        handleEventSelect={handleEventSelect}
        currentUser={currentUser}
        setSelectedSchedule={setSelectedSchedule}
        setFormData={setFormData}
        fetchSchedules={fetchSchedules}
        setModalIsOpen={setModalIsOpen}
        eventPropGetter={eventPropGetter}
      />

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={() => setModalIsOpen(false)}
        contentLabel={t("scheduleForm")}
        className="modal-content"
        overlayClassName="modal-overlay"
        style={modalStyle}
        onAfterClose={handleAfterModalClose}
      >
        <div style={{ position: "relative" }}>
          <p
            style={{
              position: "fixed",
              top: "0px",
              right: "20px",
              width: "40px",
              height: "40px",
              fontSize: "20px",
              fontWeight: "bold",
              cursor: "pointer",
              zIndex: 9999,
              color: "#000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => setModalIsOpen(false)}
          >
            &times;
          </p>

          <div style={{ position: "absolute", top: 0, left: 0, zIndex: 1 }}>
            {selectedSchedule && currentUser !== "user" && (
              <StatusChip status={selectedSchedule.status} />
            )}
          </div>

          {selectedSchedule && currentUser === "admin" && (
            <div style={{ marginTop: "40px" }}>
              <AdminViewToggle
                adminViewMode={adminViewMode}
                setAdminViewMode={setAdminViewMode}
              />
            </div>
          )}

          <div style={{ margin: "20px" }}>
            {currentUser === "admin" && adminViewMode === "form" ? (
              <ScheduleForm
                selectedSchedule={selectedSchedule}
                formData={formData}
                setFormData={setFormData}
                handleSubmit={handleSubmit}
                users={users}
                clients={clients}
                setModalIsOpen={setModalIsOpen}
                currentUser={currentUser}
              />
            ) : (
              <div
                style={{
                  display: "flex",
                  gap: "20px",
                  flexDirection: window.innerWidth <= 768 ? "column" : "row",
                }}
              >
                <ScheduleDetails
                  selectedSchedule={selectedSchedule}
                  currentUser={currentUser}
                  localNotes={localNotes}
                  handleChecklistItemChange={handleChecklistItemChange}
                  handleLocalNotesChange={handleLocalNotesChange}
                  handleSaveNotes={handleSaveNotes}
                  handleCompleteService={handleCompleteService}
                />
                <PhotoSection
                  selectedSchedule={selectedSchedule}
                  currentUser={currentUser}
                  handlePhotoUpload={handlePhotoUpload}
                  setSelectedSchedule={setSelectedSchedule}
                  handleCompleteService={handleCompleteService}
                />
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ScheduleCalendar;
